import React, { useRef, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Hls from "hls.js";
import waytora_logo from "@/assets/logos/internal/WaytoraWhiteTextLogo.svg";
import logger from "@/utils/logger";

const MobileLanding: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const videoUrl =
    "https://customer-zm6tqrwkxojntyc8.cloudflarestream.com/1f580192e7aeb418dbb571a85da8e02e/manifest/video.m3u8";

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (Hls.isSupported()) {
      const hls = new Hls({
        startLevel: -1,
        capLevelToPlayerSize: false,
        maxBufferLength: 30,
        maxLoadingDelay: 2,
      });

      hls.loadSource(videoUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch((error) => {
          logger.log("Auto-play was prevented:", error);
        });
      });

      return () => {
        hls.destroy();
      };
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = videoUrl;
      video.addEventListener("loadedmetadata", () => {
        video.play().catch((error) => {
          logger.log("Auto-play was prevented:", error);
        });
      });
    }
  }, []);

  // Menu variants for animation
  const menuVariants = {
    open: {
      opacity: 1,
      transition: {
        type: "tween",
        ease: "easeInOut",
        duration: 0.3,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
    closed: {
      opacity: 0,
      transition: {
        type: "tween",
        ease: "easeInOut",
        duration: 0.3,
        when: "afterChildren",
        staggerChildren: 0.05,
        staggerDirection: -1,
      },
    },
  };

  const itemVariants = {
    open: { opacity: 1, filter: "blur(0px)" },
    closed: { opacity: 0, filter: "blur(4px)" },
  };

  const menuItems = [
    { title: "Home", link: "/" },
    { title: "About", link: "/about" },
    { title: "Contact", link: "/contact" },
    { title: "Terms of Service", link: "/terms" },
    { title: "Privacy Policy", link: "/privacy-policy" },
  ];

  return (
    <div className="relative w-full min-h-screen flex flex-col overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0 bg-black">
        <video
          ref={videoRef}
          className="opacity-80 absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
          muted
          playsInline
          loop
        />
      </div>

      {/* Header with hamburger menu */}
      <div className="relative z-20 w-full flex justify-center items-center px-4 py-4">
        <img src={waytora_logo} alt="Waytora Logo" className="w-48" />
      </div>

      {/* Animated Menu Overlay */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="fixed inset-0 z-10 bg-black/90 backdrop-blur-md flex flex-col"
            initial="closed"
            animate="open"
            exit="closed"
            variants={menuVariants}
          >
            <div className="flex flex-col items-center justify-center h-full px-6 py-12 gap-6">
              {menuItems.map((item, index) => (
                <motion.a
                  key={index}
                  className="text-white text-2xl font-medium py-2 border-b border-white/10 w-full text-center"
                  variants={itemVariants}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {item.title}
                </motion.a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <motion.main
        className="relative z-0 flex-grow flex items-center justify-center px-4"
        animate={{
          opacity: menuOpen ? 0 : 1,
          filter: menuOpen ? "blur(8px)" : "blur(0px)",
          scale: menuOpen ? 0.98 : 1,
        }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          className="relative z-10 max-w-md w-full bg-black/40 backdrop-blur-lg rounded-2xl p-8 shadow-xl border border-white/20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* <motion.div
            className="mb-8 flex justify-center"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <img src={waytora_logo} alt="Waytora Logo" className="w-48" />
          </motion.div> */}

          <motion.h1
            className="text-3xl font-medium text-white text-center mb-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            Best Experienced on a Laptop
          </motion.h1>

          <motion.p
            className="text-white/80 text-center mb-8 font-light"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.5 }}
          >
            Waytora is designed for optimal experience on larger screens. We're
            working on a mobile app to bring the full experience to your phone
            very soon!
          </motion.p>
        </motion.div>
      </motion.main>
    </div>
  );
};

export default MobileLanding;
